import { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export interface NavOption {
  label: string;
  /** Destination path for navigation */
  to: string;
  /** If true, active check also requires localStorage selectionType to match */
  shared?: boolean;
  /** Additional path prefixes that should also mark this option as active */
  activePaths?: string[];
}

interface NavDropdownProps {
  label: string;
  options: NavOption[];
  /**
   * Used to disambiguate shared paths (e.g. /procesos, /registros).
   * The parent and shared-path items are active only when
   * localStorage "itemSelection" matches this value.
   */
  selectionType: string;
  /** When true, renders options inline (for mobile menu) instead of absolutely positioned */
  inlinePanel?: boolean;
}

const STORAGE_KEY = "itemSelection";

const getStoredType = (): string => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '"masivo"');
  } catch {
    return "masivo";
  }
};

const NavDropdown = ({ label, options, selectionType, inlinePanel = false }: NavDropdownProps) => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const storedType = getStoredType();

  const isOptionActive = (opt: NavOption): boolean => {
    const pathMatches =
      pathname.startsWith(opt.to) ||
      (opt.activePaths?.some((p) => pathname.startsWith(p)) ?? false);
    if (opt.shared) {
      return pathMatches && storedType === selectionType;
    }
    return pathMatches;
  };

  const isParentActive = options.some(isOptionActive);

  // Las opciones son enlaces reales (<a href>) para que funcionen el clic del
  // medio y "abrir en otra pestaña". El tipo de selección (masivo/profesional)
  // se guarda al presionar cualquier botón del mouse, antes de navegar: así la
  // pestaña nueva también sabe desde qué menú se abrió /procesos.
  const rememberSelectionType = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectionType));
    } catch {
      // Sin localStorage solo se pierde el resaltado del menú activo.
    }
  };

  const optionButtons = options.map((opt, i) => {
    const active = isOptionActive(opt);
    const hovered = hoveredIndex === i;
    return (
      <a
        key={opt.to}
        href={opt.to}
        onMouseEnter={() => setHoveredIndex(i)}
        onMouseLeave={() => setHoveredIndex(null)}
        onMouseDown={rememberSelectionType}
        onKeyDown={(event) => { if (event.key === "Enter") rememberSelectionType(); }}
        style={{
          display: "block",
          boxSizing: "border-box",
          textDecoration: "none",
          width: "100%",
          padding: inlinePanel ? "10px 16px 10px 28px" : "10px 20px",
          background: hovered && !active ? "#f0f6ff" : "none",
          border: "none",
          borderLeft: active ? "3px solid #005bc7" : "3px solid transparent",
          textAlign: "left",
          cursor: "pointer",
          fontFamily: "Roboto, sans-serif",
          fontSize: "14px",
          fontWeight: active ? 600 : 400,
          color: active ? "#005bc7" : "#48566b",
          transition: "background 0.1s, color 0.1s",
          borderRadius: inlinePanel ? "8px" : undefined,
        }}
      >
        {opt.label}
      </a>
    );
  });

  if (inlinePanel) {
    return (
      <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <button
          onClick={() => setOpen(!open)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "3px",
            background: "none",
            border: "none",
            borderRadius: "8px",
            borderLeft: isParentActive ? "3px solid #005bc7" : "3px solid transparent",
            cursor: "pointer",
            fontFamily: "Roboto, sans-serif",
            fontSize: "15px",
            fontWeight: isParentActive ? 600 : 400,
            color: isParentActive ? "#005bc7" : "#333333",
            padding: "10px 12px",
            width: "100%",
            textAlign: "left",
            transition: "color 0.15s, background 0.15s",
          }}
        >
          {label}
          <KeyboardArrowDownIcon
            style={{
              fontSize: 18,
              marginLeft: "auto",
              transition: "transform 0.2s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              color: isParentActive ? "#005bc7" : "#888",
            }}
          />
        </button>
        {open && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {optionButtons}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{ position: "relative", height: "100%", display: "flex", alignItems: "center" }}
      onMouseEnter={() => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setOpen(true);
      }}
      onMouseLeave={() => {
        closeTimer.current = setTimeout(() => {
          setOpen(false);
          setHoveredIndex(null);
        }, 100);
      }}
    >
      {/* Trigger button */}
      <button
        style={{
          display: "flex",
          alignItems: "center",
          gap: "3px",
          background: "none",
          border: "none",
          borderBottom: isParentActive ? "2px solid #005bc7" : "2px solid transparent",
          cursor: "pointer",
          fontFamily: "Roboto, sans-serif",
          fontSize: "15px",
          fontWeight: isParentActive ? 600 : 400,
          color: isParentActive ? "#005bc7" : "#333333",
          padding: "0 4px",
          height: "100%",
          transition: "color 0.15s, border-color 0.15s",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        <KeyboardArrowDownIcon
          style={{
            fontSize: 18,
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            color: isParentActive ? "#005bc7" : "#888",
          }}
        />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            background: "#ffffff",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
            borderRadius: "8px",
            padding: "6px 0",
            minWidth: "200px",
            zIndex: 9999,
            border: "1px solid #e5e7eb",
          }}
        >
          {optionButtons}
        </div>
      )}
    </div>
  );
};

export default NavDropdown;
